import type { Assignees, Task, Todo } from './schemas';

/**
 * A one-off job from the form "Einmal · Regelmäßig" (1.28). The data stays split as before (concept
 * 24.10.: "kein Datenumzug vor dem Launch"): without stars it is a Todo, with stars a Task that
 * happens once – only tasks book stars on the server.
 */
export interface OnceJob {
  title: string;
  icon: string;
  assignees: Assignees;
  /** YYYY-MM-DD; none = "irgendwann" */
  due?: string;
  points: number;
  createdBy?: string;
}

export type OnceEntry =
  | { kind: 'todo'; todo: Omit<Todo, 'id' | 'done'> }
  | { kind: 'task'; task: Omit<Task, 'id' | 'source' | 'active'> };

/**
 * @param keep editing keeps what the entry already is – a to-do never turns into a task on the
 *   way (that would need a new document), a one-off task with 0 stars simply books nothing
 */
export function onceEntry(job: OnceJob, today: string, keep?: OnceEntry['kind']): OnceEntry {
  const kind = keep ?? (job.points > 0 ? 'task' : 'todo');
  const { points, due, ...rest } = job;
  if (kind === 'todo') return { kind, todo: { ...rest, due } };
  return {
    kind,
    task: {
      ...rest,
      points,
      daypart: 'anytime',
      // a one-off task needs its day: without one it is today's
      repeat: { kind: 'once', date: due ?? today },
      // the same rule as the regular form: from 10 stars a parent confirms
      requiresApproval: points >= 10,
    },
  };
}
